/**
 * L1J Web Client - Character Detail Panel
 *
 * 캐릭터 상세 정보 창 (화면 좌측, 기본 숨김).
 *
 * 표시하는 값은 전부 S_OwnCharStatus(opcode 145) 하나에서 온다
 * (`PlayerDetailStats`). 좌하단 상시 HUD인 CharacterStatsPanel과 달리
 * 능력치·AC·속성 저항·무게처럼 자주 보지 않는 값을 모아 보여준다.
 */

import Phaser from 'phaser';
import { PlayerDetailStats } from '../../core/types';

/** 한 줄(라벨 + 값)의 세로 간격. */
const ROW_HEIGHT = 15;
/** 그룹과 그룹 사이 추가 여백. */
const GROUP_GAP = 6;

export class CharacterDetailPanel extends Phaser.GameObjects.Container {
  private panelWidth = 210;
  /**
   * 18줄 + 그룹 여백 4회가 모두 들어가는 높이.
   * 줄을 추가하면 여기도 같이 키워야 한다 — 넘치면 아래 항목이 조용히 잘린다.
   */
  /**
   * 20줄(그룹 간격 4회 포함 324px)이 42px 여백 아래로 들어가야 한다.
   * SP/MR 두 줄이 늘면서 340으로는 마지막 줄이 패널 밖으로 밀려 380으로 올렸다.
   */
  private panelHeight = 380;

  /** 라벨 → 값 텍스트. setStats가 이 맵을 통해 갱신한다. */
  private valueTexts = new Map<string, Phaser.GameObjects.Text>();

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);

    // HUD이므로 카메라 스크롤에 끌려가지 않게 고정한다.
    this.setScrollFactor(0);

    this.createUI();
    this.setDepth(160);
    this.setVisible(false);
  }

  /** 패널 폭. 호출부가 화면 가장자리에 정렬할 때 쓴다. */
  public getPanelWidth(): number {
    return this.panelWidth;
  }

  /** 패널 높이. 다른 창을 이어 붙일 때 쓴다. */
  public getPanelHeight(): number {
    return this.panelHeight;
  }

  private createUI(): void {
    const bg = this.scene.add.rectangle(0, 0, this.panelWidth, this.panelHeight, 0x1a1a2e, 0.95);
    bg.setStrokeStyle(2, 0x4a4a6a);
    this.add(bg);

    const titleBar = this.scene.add.rectangle(
      0,
      -this.panelHeight / 2 + 15,
      this.panelWidth,
      30,
      0x2a2a4e
    );
    titleBar.setStrokeStyle(1, 0x3a3a5a);
    this.add(titleBar);

    const title = this.scene.add.text(0, -this.panelHeight / 2 + 15, 'Character', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.add(title);

    const closeBtn = this.scene.add.text(this.panelWidth / 2 - 20, -this.panelHeight / 2 + 15, 'X', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ff6666',
    });
    closeBtn.setOrigin(0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.setScrollFactor(0);
    // 닫은 뒤 씬이 이 클릭을 월드 클릭으로 오해하지 않도록 알린다
    // (Phaser는 오브젝트 핸들러를 먼저 돌리고 씬 pointerdown을 나중에 쏜다).
    closeBtn.on('pointerdown', () => {
      this.setVisible(false);
      this.emit('panelclose');
    });
    this.add(closeBtn);

    this.createRows();
  }

  /**
   * 항목 줄을 만든다. 그룹 사이에는 여백을 둔다.
   * 값은 나중에 setStats가 채우므로 여기서는 자리만 잡는다.
   */
  private createRows(): void {
    const groups: string[][] = [
      ['Level', 'EXP'],
      ['STR', 'DEX', 'CON', 'WIS', 'INT', 'CHA'],
      ['HP', 'MP', 'AC', 'SP', 'MR'],
      ['Lawful', 'Weight', 'Food'],
      ['Fire', 'Water', 'Wind', 'Earth'],
    ];

    const left = -this.panelWidth / 2 + 14;
    const right = this.panelWidth / 2 - 14;
    let y = -this.panelHeight / 2 + 42;

    groups.forEach((group, groupIndex) => {
      if (groupIndex > 0) y += GROUP_GAP;

      group.forEach((label) => {
        const labelText = this.scene.add.text(left, y, label, {
          fontSize: '11px',
          fontFamily: 'Arial, sans-serif',
          color: '#8888aa',
        });
        labelText.setOrigin(0, 0.5);
        this.add(labelText);

        const valueText = this.scene.add.text(right, y, '-', {
          fontSize: '11px',
          fontFamily: 'Arial, sans-serif',
          color: '#ffffff',
        });
        valueText.setOrigin(1, 0.5);
        this.add(valueText);

        this.valueTexts.set(label, valueText);
        y += ROW_HEIGHT;
      });
    });
  }

  /**
   * 서버에서 받은 상세 스탯으로 화면을 갱신한다.
   *
   * 무게는 서버가 0~240 스케일로 보내므로 백분율로 환산해 보여준다
   * (원본과 같은 "현재/최대" 형태로 쓰려면 최대 무게 계산식이 따로 필요하다).
   */
  public setStats(stats: PlayerDetailStats): void {
    const set = (label: string, value: string) => {
      this.valueTexts.get(label)?.setText(value);
    };

    set('Level', String(stats.level));
    set('EXP', stats.exp.toLocaleString());

    set('STR', String(stats.str));
    set('DEX', String(stats.dex));
    set('CON', String(stats.con));
    set('WIS', String(stats.wis));
    set('INT', String(stats.intel));
    set('CHA', String(stats.cha));

    set('HP', `${stats.currentHp} / ${stats.maxHp}`);
    set('MP', `${stats.currentMp} / ${stats.maxMp}`);
    set('AC', String(stats.ac));
    // SP/MR은 S_SpMr(opcode 174)로만 온다 — 아직 못 받았으면 0이다.
    set('SP', String(stats.sp ?? 0));
    set('MR', String(stats.mr ?? 0));

    set('Lawful', String(stats.lawful));
    set('Weight', `${Math.round((stats.weight / 240) * 100)}%`);
    set('Food', `${Math.round((stats.food / 225) * 100)}%`);

    set('Fire', String(stats.fire));
    set('Water', String(stats.water));
    set('Wind', String(stats.wind));
    set('Earth', String(stats.earth));
  }

  public toggle(): void {
    this.setVisible(!this.visible);
  }
}
